import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, Download, FileUp, Trash2, X } from "lucide-react";
import { API } from "../../lib/eventUtils";

const STATUS_STYLE = {
  valid: "text-volt",
  duplicate_db: "text-amber-400",
  duplicate_file: "text-amber-400",
  invalid: "text-red-400",
};
const STATUS_LABEL = {
  valid: "Valid",
  duplicate_db: "Duplicate (already registered)",
  duplicate_file: "Duplicate (in file)",
  invalid: "Invalid",
};
const WL_STATUS_STYLE = {
  waiting: "text-volt",
  notified: "text-amber-400",
  registered: "text-zinc-500",
};

export const RosterPanel = ({ event, onClose, onChanged }) => {
  const [rows, setRows] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [regs, wl] = await Promise.all([
        axios.get(`${API}/api/admin/events/${event.event_id}/registrations`, { withCredentials: true }),
        axios.get(`${API}/api/admin/events/${event.event_id}/waitlist`, { withCredentials: true }),
      ]);
      setRows(regs.data);
      setWaitlist(wl.data);
    } catch {
      toast.error("Could not load the roster");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.event_id]);

  const removeReg = async (r) => {
    if (!window.confirm(`Remove ${r.first_name} ${r.last_name} (${r.email}) from the roster? This frees a spot and notifies the waitlist.`)) return;
    try {
      const { data } = await axios.delete(`${API}/api/admin/events/${event.event_id}/registrations/${r.registration_id}`, { withCredentials: true });
      toast.success(
        data.waitlist_notifying > 0
          ? `Removed — emailing ${data.waitlist_notifying} waitlisted athlete${data.waitlist_notifying === 1 ? "" : "s"} that a spot opened`
          : "Registration removed"
      );
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/events/${event.event_id}/registrations/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${event.slug}-roster.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCsvText(String(reader.result));
      setPreview(null);
    };
    reader.readAsText(file);
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/events/${event.event_id}/registrations/import-preview`,
        { csv_text: csvText },
        { withCredentials: true }
      );
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not parse the CSV");
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/events/${event.event_id}/registrations/import`,
        { csv_text: csvText },
        { withCredentials: true }
      );
      toast.success(`Imported ${data.imported} registrations (${data.duplicates} duplicates, ${data.invalid} invalid skipped)`);
      setPreview(null);
      setCsvText("");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-white/15 bg-[#0a0a0a] p-6 sm:p-8 mb-8" data-testid="roster-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <p className="eyebrow mb-1">Roster</p>
          <h2 className="font-head font-extrabold uppercase text-2xl tracking-tight">{event.title}</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="roster-export-button">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors px-2" data-testid="roster-close" aria-label="Close roster">
            <X size={20} />
          </button>
        </div>
      </div>

      {rows === null ? (
        <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-zinc-500 text-sm mb-6" data-testid="roster-empty">No registrations yet.</p>
      ) : (
        <div className="overflow-x-auto mb-8">
          <table className="w-full text-sm" data-testid="roster-table">
            <thead>
              <tr className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10 text-left">
                <th className="py-2 pr-4">#</th>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Phone</th>
                <th className="py-2 pr-4">Instagram</th>
                <th className="py-2 pr-4">Source</th>
                <th className="py-2 pr-4">Paid</th>
                <th className="py-2 pr-4">Registered</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.registration_id} className="border-b border-white/5 text-zinc-300" data-testid={`roster-row-${i}`}>
                  <td className="py-2.5 pr-4 text-zinc-600 font-mono2 text-xs">{i + 1}</td>
                  <td className="py-2.5 pr-4 font-semibold text-white">{r.first_name} {r.last_name}</td>
                  <td className="py-2.5 pr-4">{r.email}</td>
                  <td className="py-2.5 pr-4">{r.phone || "—"}</td>
                  <td className="py-2.5 pr-4">{r.instagram ? `@${r.instagram}` : "—"}</td>
                  <td className="py-2.5 pr-4">
                    <span className={`font-mono2 text-[10px] uppercase tracking-[0.15em] ${r.source === "import" ? "text-amber-400" : "text-volt"}`}>{r.source}</span>
                  </td>
                  <td className="py-2.5 pr-4 font-mono2 text-xs" data-testid={`roster-paid-${i}`}>
                    {r.amount_paid_cents ? <span className="text-volt">${(r.amount_paid_cents / 100).toFixed(2)}</span> : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="py-2.5 pr-4 text-zinc-500 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="py-2.5 text-right">
                    <button onClick={() => removeReg(r)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid={`roster-remove-${i}`} aria-label={`Remove ${r.first_name} ${r.last_name}`} title="Remove & free spot">
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* WAITLIST */}
      <div className="border-t border-white/10 pt-6 mb-8" data-testid="waitlist-section">
        <p className="eyebrow mb-3 flex items-center gap-2"><BellRing size={13} /> Waitlist ({waitlist.length})</p>
        {waitlist.length === 0 ? (
          <p className="text-zinc-600 text-xs" data-testid="waitlist-empty">Nobody on the waitlist. It opens automatically once the event sells out.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="waitlist-table">
              <thead>
                <tr className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10 text-left">
                  <th className="py-2 pr-4">#</th>
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Phone</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2">Joined</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.map((w, i) => (
                  <tr key={w.waitlist_id} className="border-b border-white/5 text-zinc-300" data-testid={`waitlist-row-${i}`}>
                    <td className="py-2.5 pr-4 text-zinc-600 font-mono2 text-xs">{i + 1}</td>
                    <td className="py-2.5 pr-4 font-semibold text-white">{w.first_name} {w.last_name}</td>
                    <td className="py-2.5 pr-4">{w.email}</td>
                    <td className="py-2.5 pr-4">{w.phone || "—"}</td>
                    <td className={`py-2.5 pr-4 font-mono2 text-[10px] uppercase tracking-[0.15em] ${WL_STATUS_STYLE[w.status] || "text-zinc-400"}`}>
                      {w.status === "notified" ? "Spot-open email sent" : w.status}
                    </td>
                    <td className="py-2.5 text-zinc-500 text-xs">{(w.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CSV IMPORT */}
      <div className="border-t border-white/10 pt-6" data-testid="csv-import-section">
        <p className="eyebrow mb-2 flex items-center gap-2"><FileUp size={13} /> Import registrations (CSV)</p>
        <p className="text-zinc-500 text-xs leading-relaxed mb-4 max-w-2xl">
          Upload the legacy export. Columns mapped: first_name, last_name, email, instagram, phone, created_at
          (original timestamps are preserved). Legacy merch / size / clips / consent columns are ignored.
          Duplicates and invalid rows are reported before anything is written.
        </p>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-xs text-zinc-400 file:mr-3 file:btn-ghost file:border file:border-white/20 file:bg-transparent file:text-white file:px-3 file:py-1.5 file:text-xs file:uppercase file:font-mono2 file:cursor-pointer" data-testid="csv-file-input" />
          <button onClick={runPreview} disabled={!csvText || busy} className="btn-ghost !py-2 !px-4 !text-sm disabled:opacity-40" data-testid="csv-preview-button">
            {busy && !preview ? "Parsing…" : "Preview Import"}
          </button>
        </div>

        {preview && (
          <div data-testid="csv-preview-results">
            <div className="flex flex-wrap gap-4 mb-4 font-mono2 text-[11px] uppercase tracking-[0.15em]" data-testid="csv-preview-summary">
              <span className="text-zinc-300">{preview.summary.total} rows</span>
              <span className="text-volt">{preview.summary.valid} valid</span>
              <span className="text-amber-400">{preview.summary.duplicates} duplicates</span>
              <span className="text-red-400">{preview.summary.invalid} invalid</span>
              {preview.ignored_columns.length > 0 && <span className="text-zinc-600">ignored: {preview.ignored_columns.join(", ")}</span>}
            </div>
            <div className="overflow-x-auto max-h-80 overflow-y-auto border border-white/10 mb-4">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#0a0a0a]">
                  <tr className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10 text-left">
                    <th className="py-2 px-3">Row</th>
                    <th className="py-2 px-3">Name</th>
                    <th className="py-2 px-3">Email</th>
                    <th className="py-2 px-3">Instagram</th>
                    <th className="py-2 px-3">Created</th>
                    <th className="py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row} className="border-b border-white/5 text-zinc-300" data-testid={`csv-preview-row-${r.row}`}>
                      <td className="py-2 px-3 text-zinc-600">{r.row}</td>
                      <td className="py-2 px-3">{r.first_name} {r.last_name}</td>
                      <td className="py-2 px-3">{r.email}</td>
                      <td className="py-2 px-3">{r.instagram ? `@${r.instagram}` : "—"}</td>
                      <td className="py-2 px-3 text-zinc-500">{(r.created_at || "").slice(0, 10)}</td>
                      <td className={`py-2 px-3 font-mono2 text-[10px] uppercase tracking-[0.1em] ${STATUS_STYLE[r.status]}`}>
                        {STATUS_LABEL[r.status]}{r.reason ? ` — ${r.reason}` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={runImport} disabled={busy || preview.summary.valid === 0} className="btn-volt disabled:opacity-40" data-testid="csv-import-button">
              {busy ? "Importing…" : `Import ${preview.summary.valid} valid rows`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
